import { css } from '@compiled/react';
import { type IntlShape } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';

import { type EmailSubscriptionStatus } from '../hooks/use-submit-email-subscription';

import { emailSubscriptionFormMessages } from './form-messages';

export interface EmailSubscriptionStatusMessageContent {
	text: string;
	tone: 'error' | 'success';
}

export interface GetStatusMessageArgs {
	intl: IntlShape;
	isEmailInvalid: boolean;
	status: EmailSubscriptionStatus;
}

export const getStatusMessage = (
	args: GetStatusMessageArgs,
): EmailSubscriptionStatusMessageContent | undefined => {
	const { intl, isEmailInvalid, status } = args;

	if (isEmailInvalid) {
		return { text: intl.formatMessage(emailSubscriptionFormMessages.invalidEmail), tone: 'error' };
	}

	if (status === 'error') {
		return {
			text: intl.formatMessage(emailSubscriptionFormMessages.unexpectedError),
			tone: 'error',
		};
	}

	if (status === 'success') {
		return { text: intl.formatMessage(emailSubscriptionFormMessages.success), tone: 'success' };
	}

	return undefined;
};

export interface EmailSubscriptionStatusMessageProps {
	errorId: string;
	message: EmailSubscriptionStatusMessageContent;
}

export const EmailSubscriptionStatusMessage = (props: EmailSubscriptionStatusMessageProps) => {
	const { errorId, message } = props;

	const isError = message.tone === 'error';

	return (
		<span
			aria-live={isError ? undefined : 'polite'}
			css={[statusMessageCss, isError && errorCss]}
			id={isError ? errorId : undefined}
			role={isError ? 'alert' : 'status'}
		>
			{message.text}
		</span>
	);
};

// A span rather than a paragraph: TypographyStyles targets `p` as a descendant,
// which outranks a single class and would reset size and colour. Spans the form's
// grid so it always sits on its own row.
const statusMessageCss = css({
	display: 'block',
	fontSize: '0.875rem',
	gridColumn: '1 / -1',
	lineHeight: '1.25rem',
	margin: 0,
});

const errorCss = css({
	color: wacToken('color.secondary.red'),
});
